import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api, { subscriptionAPI, SubscriptionStatus } from '../../services/api';
import { CreditCard, Calendar, AlertCircle, ExternalLink, Crown } from 'lucide-react';
import { formatBillingDate as formatDate } from '../../utils/format';

const SubscriptionSection: React.FC = () => {
  const { user } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const freeUsage = subscriptionStatus?.free_tier_usage;

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        const response = await subscriptionAPI.getStatus();
        setSubscriptionStatus(response);
      } catch (error: any) {
        console.error('Failed to fetch subscription status:', error);
        setError('Failed to load subscription information');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscriptionStatus();
  }, []);

  const handleManageSubscription = async () => {
    if (subscriptionStatus?.billing_management !== 'web') {
      setError('No web billing subscription is available to manage.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const response = await api.post('/subscriptions/customer-portal');
      window.location.href = response.data.url;
    } catch (error: any) {
      console.error('Failed to open customer portal:', error);
      setError('Failed to open subscription management. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const getPlanDisplayName = (plan: SubscriptionStatus['plan']) => {
    switch (plan) {
      case 'monthly':
        return "Cracked At monthly ($19.99/month)";
      case 'annual':
        return "Cracked At annual ($199.99/year)";
      case 'year12':
        return 'Year 12 pass';
      case 'year11_12':
        return 'Year 11 + 12 pass';
      case 'lifetime':
        return 'Lifetime pass';
      default:
        return 'Cracked At Pro';
    }
  };

  const isWebBillingSubscription = subscriptionStatus?.billing_management === 'web';
  const isPass = ['year12', 'year11_12', 'lifetime'].includes(subscriptionStatus?.plan ?? '');

  if (isLoading) {
    return (
      <div className="max-w-2xl">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mango-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Subscription Management
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Manage your subscription, billing, and payment methods.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
          </div>
        </div>
      )}

      {/* Admin Status */}
      {user?.is_admin && (
        <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
          <div className="flex items-center space-x-2">
            <Crown className="h-5 w-5 text-purple-500" />
            <div>
              <h4 className="text-sm font-medium text-purple-700 dark:text-purple-300">
                Administrator Account
              </h4>
              <p className="text-xs text-purple-600 dark:text-purple-400">
                You have full access to all features as an administrator.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current Subscription Status */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 mb-6">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Current Subscription
        </h4>

        {subscriptionStatus?.has_active_subscription ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Plan:</span>
              <span className="text-sm text-gray-900 dark:text-white">
                {getPlanDisplayName(subscriptionStatus.plan)}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</span>
              <span className={`text-sm font-medium ${
                subscriptionStatus.access === 'active'
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-yellow-600 dark:text-yellow-400'
              }`}>
                {subscriptionStatus.access === 'active' ? 'Active' : 'Free'}
              </span>
            </div>

            {subscriptionStatus.current_period_end && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {subscriptionStatus.cancel_at_period_end ? 'Ends:' : 'Renews:'}
                </span>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-900 dark:text-white">
                    {formatDate(subscriptionStatus.current_period_end)}
                  </span>
                </div>
              </div>
            )}

            {subscriptionStatus.cancel_at_period_end && !isPass && (
              <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded border border-yellow-200 dark:border-yellow-800">
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  Your subscription will not renew and will end on {formatDate(subscriptionStatus.current_period_end!)}.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                You’re currently using the free plan. Enjoy limited daily access to Practice, Survival, and Rush modes. Upgrade to Cracked At Pro for unlimited sessions, advanced analytics, and the complete question library.
              </p>
            </div>
            {freeUsage && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-300">
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                  <p className="font-semibold text-gray-800 dark:text-gray-100">Practice Questions</p>
                  <p>{freeUsage.practice_questions_used}/{freeUsage.practice_questions_limit} used today</p>
                </div>
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                  <p className="font-semibold text-gray-800 dark:text-gray-100">Survival Runs</p>
                  <p>{freeUsage.survival_sessions_used}/{freeUsage.survival_sessions_limit} used today</p>
                </div>
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                  <p className="font-semibold text-gray-800 dark:text-gray-100">Rush Runs</p>
                  <p>{freeUsage.rush_sessions_used}/{freeUsage.rush_sessions_limit} used today</p>
                </div>
              </div>
            )}
            <button
              onClick={() => window.location.href = '/subscription'}
              className="inline-flex items-center px-4 py-2 bg-mango-600 text-white text-sm font-medium rounded-md hover:bg-mango-700 transition-colors"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Upgrade to Pro
            </button>
          </div>
        )}
      </div>

      {subscriptionStatus?.has_active_subscription && subscriptionStatus.billing_management === 'app_store' && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-6 mb-6">
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            App Store Subscription
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            This subscription was purchased through the App Store. Manage renewal, cancellation, and payment details from your Apple ID subscription settings on iPhone or iPad.
          </p>
        </div>
      )}

      {/* Manage Subscription Button */}
      {subscriptionStatus?.has_active_subscription && isWebBillingSubscription && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Subscription Management
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Manage your billing information, payment methods, download invoices, or cancel your subscription
            through the secure billing portal.
          </p>
          <button
            onClick={handleManageSubscription}
            disabled={isProcessing}
            className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium transition-colors ${
              isProcessing
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-mango-600 text-white hover:bg-mango-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mango-500'
            }`}
          >
            {isProcessing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Opening...
              </>
            ) : (
              <>
                <ExternalLink className="h-4 w-4 mr-2" />
                Manage Subscription
              </>
            )}
          </button>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            You'll be redirected to the secure billing portal
          </p>
        </div>
      )}

      {/* Features List */}
      <div className="mt-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Subscription Benefits
        </h4>
        <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <li className="flex items-center space-x-2">
            <span className="h-1.5 w-1.5 bg-mango-500 rounded-full"></span>
            <span>Access to all VCE Methods and Specialist practice questions</span>
          </li>
          <li className="flex items-center space-x-2">
            <span className="h-1.5 w-1.5 bg-mango-500 rounded-full"></span>
            <span>Personalized difficulty matching</span>
          </li>
          <li className="flex items-center space-x-2">
            <span className="h-1.5 w-1.5 bg-mango-500 rounded-full"></span>
            <span>Detailed performance analytics</span>
          </li>
          <li className="flex items-center space-x-2">
            <span className="h-1.5 w-1.5 bg-mango-500 rounded-full"></span>
            <span>Game modes: Survival, Rush, and more</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default SubscriptionSection;
